import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { Button, ErrorText, Kicker, Muted } from '@/shared/ui';
import { RumFields, useRumFields } from '@/shared/rum/RumFields';
import { RumPhotoField } from '@/shared/rum/RumPhotoField';
import styles from '@/shared/rum/rum-fields.module.scss';

/**
 * Écran 09 — nouvelle fiche rhum (US-05).
 *
 * Seuls le nom et le type sont obligatoires : une bouteille rapportée d'un voyage n'a
 * souvent ni âge ni prix connus, et bloquer la création sur ces champs ferait qu'aucune
 * fiche ne serait créée en séance.
 *
 * Le scan de code-barres et le pré-remplissage Open Food Facts (US-25) relèvent du lot 3 :
 * le champ code-barres existe déjà en base et se saisit ici à la main.
 */
export function NewRumScreen(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();

  /**
   * Le retour vers la séance.
   *
   * On arrive ici depuis « J'apporte une bouteille » ou « + Rattacher un rhum » : la fiche
   * créée doit rejoindre le programme, et l'on doit revenir d'où l'on vient. Sans ces deux
   * paramètres, l'écran reste ce qu'il était — la création d'une fiche pour la cave.
   */
  const [params] = useSearchParams();
  const attachTo = params.get('seance');
  const fromRegie = params.get('regie') === '1';
  // Le choix « à l'aveugle » fait avant de venir créer la fiche : sans lui, la bouteille
  // rejoindrait le programme à découvert alors que le membre avait demandé l'inverse.
  const blind = params.get('aveugle') === '1';

  const fields = useRumFields();
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();

    const parsed = fields.build();
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const created = await api.rums.create(parsed.value);
      // La photo part après : si son envoi échoue, la fiche existe malgré tout et l'on
      // peut la reprendre depuis l'écran de la bouteille. L'inverse perdrait la saisie.
      if (photo) {
        try {
          await api.rums.setPhoto(created.id, photo);
        } catch {
          setError('Fiche créée, mais la photo n’a pas pu être envoyée. Reprenez-la depuis sa page.');
        }
      }
      if (attachTo) {
        // Le rattachement peut échouer — séance close entre-temps, programme complet.
        // La fiche est créée dans tous les cas : on le dit et on laisse sur la bouteille.
        try {
          if (fromRegie) {
            // La position se calcule : la coder en dur entrerait en collision avec un
            // rhum déjà inscrit, l'index étant unique par séance.
            const seance = await api.sessions.byId(attachTo);
            await api.admin.attachRum(attachTo, {
              rumId: created.id,
              orderIndex: seance.rums.length + 1,
              isBlind: false,
            });
          } else {
            await api.sessions.contributeRum(attachTo, created.id, blind);
          }
          void navigate(fromRegie ? `/regie/seance/${attachTo}` : '/');
          return;
        } catch (cause) {
          setError(`Fiche créée, mais pas rattachée à la séance : ${errorMessage(cause)}`);
          void navigate(`/rhums/${created.id}`);
          return;
        }
      }

      void navigate(`/rhums/${created.id}`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={styles.page} onSubmit={(event) => void submit(event)} noValidate>
      <header>
        <Kicker>Nouvelle fiche</Kicker>
        <h1>Ajouter une bouteille à la cave</h1>
        <Muted>
          {attachTo
            ? 'Elle rejoindra le programme de la séance, et la cave par la même occasion.'
            : 'Seuls le nom et le type sont obligatoires.'}
        </Muted>
      </header>

      <RumFields fields={fields} />

      <fieldset className={styles.group}>
        <legend>Photo</legend>
        {/*
          La fiche n'existe pas encore : on garde le fichier de côté et on l'envoie juste
          après sa création. L'inverse — créer, rediriger, puis demander la photo —
          obligerait à revenir sur ses pas au moment où l'on a la bouteille en main.
        */}
        <RumPhotoField rumId={null} photoUrl={null} onPicked={setPhoto} />
      </fieldset>

      <Button variant="dashed" block disabled>
        Scanner un code-barres · lot 3
      </Button>
      <Muted>
        Le pré-remplissage par Open Food Facts arrive plus tard. En attendant, le code se
        saisit à la main et reste attaché à la fiche.
      </Muted>

      <ErrorText>{error}</ErrorText>
      <Button type="submit" disabled={busy}>
        {busy
          ? 'Enregistrement…'
          : attachTo
            ? 'Ajouter à la séance'
            : 'Ajouter à la cave'}
      </Button>
      <Muted>Statut initial « à déguster ».</Muted>
    </form>
  );
}
